<template>
    <div class="graph-card card" :class="{ 'graph-fullscreen': isFullscreen }">
        <!-- Toolbar -->
        <div class="graph-toolbar" v-if="hasGraph">
            <div class="toolbar-left">
                <span class="graph-stats">
                    {{ graphStats.nodes }} nodes &middot; {{ graphStats.edges }} edges
                    <template v-if="showBridgeStats && graphStats.sharedEntities">
                        &middot; {{ graphStats.sharedEntities }} in both lists
                    </template>
                    <template v-if="curationGraph && graphStats.collapsedEntities > 0">
                        &middot; {{ graphStats.collapsedEntities }} ids truncated (bundle / panel cap)
                    </template>
                </span>
            </div>
            <div class="toolbar-right">
                <button class="btn btn-sm btn-light dark-mode-btn" @click="zoomIn" title="Zoom In"><i class="fa-solid fa-magnifying-glass-plus"></i></button>
                <button class="btn btn-sm btn-light dark-mode-btn" @click="zoomOut" title="Zoom Out"><i class="fa-solid fa-magnifying-glass-minus"></i></button>
                <button class="btn btn-sm btn-light dark-mode-btn" @click="fitToScreen" title="Fit"><i class="fa-solid fa-down-left-and-up-right-to-center"></i></button>
                <button class="btn btn-sm btn-light dark-mode-btn" @click="resetView" title="Reset"><i class="fa-solid fa-rotate-left"></i></button>
                <div class="toolbar-divider"></div>
                <button class="btn btn-sm btn-light dark-mode-btn" @click="toggleFullscreen">
                    <i :class="isFullscreen ? 'fa-solid fa-compress' : 'fa-solid fa-expand'"></i>
                </button>
            </div>
        </div>
        <div
            v-if="hasGraph && curationGraph && graphInspector.visible"
            class="graph-inspector-bar px-3 py-2 small border-bottom"
            role="status"
            aria-live="polite"
        >
            <span class="graph-inspector-title fw-semibold me-2">{{ graphInspector.title }}</span>
            <span class="graph-inspector-lines text-muted">
                <template v-for="(ln, i) in graphInspector.lines" :key="i">
                    <span v-if="i > 0" class="mx-1">&middot;</span>{{ ln }}
                </template>
            </span>
        </div>

        <!-- Legend -->
        <div class="graph-legend" v-if="hasGraph">
            <template v-for="item in legendItems" :key="item.type">
                <button
                    v-if="!item.legendOverflow"
                    type="button"
                    class="legend-item"
                    :class="{ 'legend-item-disabled': hiddenTypes.has(item.type) }"
                    @click="toggleType(item.type)"
                >
                    <span class="legend-dot" :style="{ backgroundColor: hiddenTypes.has(item.type) ? '#ccc' : item.color }"></span>
                    <span class="legend-label">{{ item.label }}</span>
                    <span class="legend-count">({{ item.count }})</span>
                </button>
                <span v-else class="legend-item legend-overflow" role="note">
                    <span class="legend-label text-muted">{{ item.label }}</span>
                </span>
            </template>
        </div>

        <!-- SVG container -->
        <div ref="graphContainer" class="graph-container"></div>

        <!-- Detail panel -->
        <div
            class="detail-panel"
            :class="{
                'detail-panel--wide':
                    curationGraph && (detailNode.type === 'entity_group' || detailNode.type === 'entity_bundle'),
            }"
            v-if="detailNode"
        >
            <div class="detail-panel-header">
                <strong>{{
                    curationGraph && detailNode.type === 'entity_group' && detailNode.neighborType
                        ? detailNode.neighborType
                        : curationGraph && detailNode.type === 'entity_bundle'
                          ? labels.entity_bundle || 'Collapsed entities'
                          : labels[detailNode.type] || detailNode.type
                }}</strong>
                <button class="btn-close btn-close-white btn-sm" @click="detailNode = null"></button>
            </div>
            <div class="detail-panel-body">
                <template v-if="curationGraph && detailNode.type === 'entity_group'">
                    <p class="small text-muted mb-2">
                        <span class="font-monospace">{{ detailNode.neighborType || detailNode.key || 'type~domain' }}</span>
                        — {{ entityGroupDetailIds.length }} id(s). Ids in this group:
                    </p>
                    <div
                        v-if="entityGroupDetailIds.length"
                        class="detail-domain-list detail-entity-group-scroll font-monospace small mb-0"
                    >
                        <ul class="list-unstyled mb-0">
                            <li
                                v-for="(id, ii) in entityGroupDetailIds"
                                :key="'egid-' + ii"
                                class="py-1 border-bottom border-secondary border-opacity-25 text-break"
                            >
                                {{ id }}
                            </li>
                        </ul>
                    </div>
                    <p v-else class="small text-muted mb-0">No ids parsed for this group.</p>
                </template>
                <template v-else-if="curationGraph && detailNode.type === 'entity_bundle'">
                    <p class="small text-muted mb-2">
                        Large entity lists are sampled on the graph for performance. Download or expand the list below to
                        see every collapsed id.
                    </p>
                    <div class="mb-1"><span class="detail-label">Total in list:</span> {{ detailNode.bundleTotal }}</div>
                    <div class="mb-1"><span class="detail-label">Shown as dots:</span> {{ detailNode.bundleDisplayed }}</div>
                    <div class="mb-2"><span class="detail-label">Collapsed into this node:</span> {{ detailNode.bundleOverflow }}</div>
                    <div
                        v-if="(detailNode.bundleHiddenAllIds || []).length"
                        class="d-flex flex-wrap gap-2 mb-2"
                    >
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-primary"
                            @click="downloadTextFile('graph-curation-collapsed-entity-ids.txt', (detailNode.bundleHiddenAllIds || []).join('\n'))"
                        >
                            Download all collapsed IDs (.txt)
                        </button>
                        <button
                            v-if="(detailNode.bundleHiddenAllIds || []).length > (detailNode.bundleHiddenPreview || []).length"
                            type="button"
                            class="btn btn-sm btn-outline-secondary"
                            @click="bundlePanelShowAll = !bundlePanelShowAll"
                        >
                            {{ bundlePanelShowAll ? 'Show preview only' : `Show all ${(detailNode.bundleHiddenAllIds || []).length} in panel` }}
                        </button>
                    </div>
                    <div v-if="bundleCollapsedIdsDisplayed.length" class="detail-label mb-1">
                        Collapsed IDs ({{ bundleCollapsedIdsDisplayed.length }}<template v-if="!bundlePanelShowAll && detailNode.bundleHiddenNotListed > 0">
                            of {{ (detailNode.bundleHiddenAllIds || []).length }}; {{ detailNode.bundleHiddenNotListed }} more hidden until you expand or download</template>)
                    </div>
                    <ul
                        v-if="bundleCollapsedIdsDisplayed.length"
                        class="detail-domain-list list-unstyled mb-0 font-monospace small"
                    >
                        <li
                            v-for="(id, i) in bundleCollapsedIdsDisplayed"
                            :key="i"
                            class="py-1 border-bottom border-secondary border-opacity-25 text-break"
                        >
                            {{ id }}
                        </li>
                    </ul>
                </template>
                <template v-else-if="curationGraph && detailNode.type === 'entity'">
                    <p class="small text-muted mb-2">Entity id</p>
                    <div class="font-monospace small text-break">{{ detailNode.key }}</div>
                    <p v-if="detailNode.inBothClusters" class="small text-muted mt-2 mb-0">
                        This id appears in both cluster entity lists.
                    </p>
                    <p v-if="detailNode.mergePreviewNew" class="small text-success mt-2 mb-0">
                        New from cluster 2 (merge preview).
                    </p>
                </template>
                <template v-else-if="detailNode.type === 'entity_group'">
                    <div class="small text-muted">
                        Neighbor group node. Enable <strong>curation graph</strong> to show type on the node and domains in the side panel.
                    </div>
                    <div v-if="detailNode.neighborType" class="mt-2">
                        <span class="detail-label">Neighbor type:</span> {{ detailNode.neighborType }}
                    </div>
                </template>
                <template v-else>
                    <div><span class="detail-label">ID:</span> {{ idrVisualiseMode ? detailNode.key : detailNode.id }}</div>
                    <div><span class="detail-label">Type:</span> {{ detailNode.type }}</div>
                    <div><span class="detail-label">Domain:</span> {{ detailNode.domain }}</div>
                    <div v-if="!idrVisualiseMode"><span class="detail-label">Key:</span> {{ detailNode.key }}</div>
                    <div v-if="detailNode.entityCount != null">
                        <span class="detail-label">Entities in list:</span> {{ detailNode.entityCount }} (for this cluster hub)
                    </div>
                    <div v-if="detailNode.inBothClusters">
                        <span class="detail-label">Overlap:</span> This id appears in <strong>both</strong> cluster entity lists
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import * as d3 from "d3";

/** Graph Curation: max legend chips before "+N more kinds" (large neighbor-group graphs). */
const CURATION_GRAPH_LEGEND_MAX = 100;

const NODE_COLORS = {
    prsn: "#3366cc", cookie: "#f39c12", ck: "#f39c12", statid: "#2ecc71",
    ip: "#9b59b6", hh: "#e74c3c", org: "#e74c3c", c_type: "#3366cc", hip: "#9b59b6",
    entity_group: "#8899aa",
    entity_bundle: "#6610f2",
};
const NODE_LABELS = {
    prsn: "Person", cookie: "Cookie", ck: "Cookie", statid: "Stat ID",
    ip: "IP Address", hh: "Household", org: "Organization", c_type: "C Type", hip: "HIP",
    entity_group: "Neighbor group",
    entity_bundle: "Collapsed entities",
};

export default {
    name: "D3ForceGraph",
    props: {
        data: { type: Object, default: null },
        nodeColors: { type: Object, default: () => ({}) },
        nodeLabels: { type: Object, default: () => ({}) },
        /** When true, toolbar may show “in both lists” for Graph Curation–style graphs. Other pages omit this prop (default false). */
        showBridgeStats: { type: Boolean, default: false },
        /**
         * Graph Curation only: inspector bar, type~domain grouping colors, id counts on nodes, merge preview styling.
         * Other consumers must leave this false so behavior stays unchanged.
         */
        curationGraph: { type: Boolean, default: false },
        /**
         * Sometimes, IDR Graph might give same entity id for more than 1 node. So, IDR Visaulise view sends data with entity ID like `type~domain~key.`
         * Need not display entire ID in this case. This prop helps to identify such cases and hide key in detail panel and display id appropriately for better readability.
         */
        idrVisualiseMode: { type: Boolean, default: false },
    },
    emits: ["graph-updated"],
    data() {
        return {
            hasGraph: false,
            isFullscreen: false,
            graphStats: { nodes: 0, edges: 0, sharedEntities: 0, collapsedEntities: 0 },
            legendItems: [],
            hiddenTypes: new Set(),
            /** When true, collapsed bundle panel lists every id in bundleHiddenAllIds (scroll). */
            bundlePanelShowAll: false,
            /** Curation: hover summary under toolbar (replaces floating tooltip). */
            graphInspector: { visible: false, title: "", lines: [] },
            graphInspectorHideTimer: null,
            detailNode: null,
            simulation: null,
            svg: null,
            zoomBehavior: null,
            graphNodes: [],
            graphEdges: []
        };
    },
    computed: {
        colors() { return { ...NODE_COLORS, ...this.nodeColors }; },
        labels() { return { ...NODE_LABELS, ...this.nodeLabels }; },
        isDark() { return this.$store.state.darkMode; },
        bundleCollapsedIdsDisplayed() {
            const d = this.detailNode;
            if (!d || d.type !== "entity_bundle") return [];
            const all = d.bundleHiddenAllIds;
            const prev = d.bundleHiddenPreview || [];
            if (Array.isArray(all) && all.length && this.bundlePanelShowAll) return all;
            return prev;
        },
        /** Curation neighbor-group node: flat list of ids for the detail panel. */
        entityGroupDetailIds() {
            const d = this.detailNode;
            if (!d || d.type !== "entity_group") return [];
            if (d.entityIds && d.entityIds.length) {
                return d.entityIds.map(String).filter(Boolean);
            }
            const subs = d.entitySubgroups || [];
            const flat = subs.flatMap((sg) => (sg.entityIds || []).map(String)).filter(Boolean);
            return [...new Set(flat)];
        },
    },
    watch: {
        detailNode() {
            this.bundlePanelShowAll = false;
        },
        data: {
            handler(val) {
                if (val && val.nodes && val.edges) {
                    this.$nextTick(() => this.loadGraph(val));
                }
            },
            deep: true
        },
        isDark() {
            // Redraw to pick up new link/tooltip colors
            if (this.graphNodes.length > 0) {
                this.$nextTick(() => this.drawGraph());
            }
        },
    },
    mounted() {
        // Clean up any lingering tooltips in the DOM upon mount
        document.querySelectorAll('.graph-tooltip').forEach(el => el.remove());

        if (this.data && this.data.nodes && this.data.edges) {
            this.$nextTick(() => this.loadGraph(this.data));
        }

        // Re-fit graph whenever the container is resized (e.g. flex height settling after CSS changes).
        if (typeof ResizeObserver !== 'undefined' && this.$refs.graphContainer) {
            let _roTimer = null;
            this._resizeObserver = new ResizeObserver(() => {
                if (_roTimer) clearTimeout(_roTimer);
                _roTimer = setTimeout(() => {
                    _roTimer = null;
                    if (this.svg && this.hasGraph) this.fitToScreen();
                }, 120);
            });
            this._resizeObserver.observe(this.$refs.graphContainer);
        }
    },
    beforeUnmount() {
        if (this.simulation) this.simulation.stop();
        this.clearGraphInspectorTimer();
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
            this._resizeObserver = null;
        }
        // Ensure the tooltip is destroyed when leaving the component
        document.querySelectorAll('.graph-tooltip').forEach(el => el.remove());
    },
    methods: {
        getColor(type) { return this.colors[type] || "#95a5a6"; },

        nodeCircleStroke(d) {
            if (d.pinned) return { color: "#f39c12", width: 3 };
            if (d.mergePreviewNew) return { color: "#198754", width: 3 };
            if (d.inBothClusters) return { color: "#ffc107", width: 3 };
            return { color: this.isDark ? "#1a1d23" : "#fff", width: 2 };
        },

        downloadTextFile(filename, text) {
            const blob = new Blob([text == null ? "" : String(text)], { type: "text/plain;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.rel = "noopener";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        },

        clearGraphInspectorTimer() {
            if (this.graphInspectorHideTimer != null) {
                clearTimeout(this.graphInspectorHideTimer);
                this.graphInspectorHideTimer = null;
            }
        },

        scheduleGraphInspectorHide() {
            this.clearGraphInspectorTimer();
            this.graphInspectorHideTimer = setTimeout(() => {
                this.graphInspectorHideTimer = null;
                this.graphInspector = { visible: false, title: "", lines: [] };
            }, 450);
        },

        connectionCountForNode(d) {
            return this.graphEdges.filter((e) => {
                const s = typeof e.source === "object" ? e.source.id : e.source;
                const t = typeof e.target === "object" ? e.target.id : e.target;
                return s === d.id || t === d.id;
            }).length;
        },

        setGraphInspectorFromNode(d) {
            const conns = this.connectionCountForNode(d);
            const lines = [];
            let title = "";
            if (this.curationGraph && d.type === "entity_group") {
                title = d.neighborType || d.key || this.labels.entity_group || "Group";
                const ids = (d.entityIds && d.entityIds.length
                    ? d.entityIds
                    : (d.entitySubgroups || []).flatMap((sg) => sg.entityIds || []).map(String).filter(Boolean));
                const cap = 40;
                const shown = ids.slice(0, cap);
                shown.forEach((id) => lines.push(String(id)));
                if (ids.length > cap) {
                    lines.push(`… +${ids.length - cap} more ids`);
                } else if (!ids.length) {
                    lines.push("—");
                }
                lines.push(`IDs total: ${ids.length}`);
                lines.push(`Connections: ${conns}`);
                if (d.mergePreviewNew) lines.push("New from cluster 2 in merge preview");
                lines.push("Click for full id list");
            } else if (this.curationGraph && d.type === "entity_bundle") {
                title = this.labels.entity_bundle || "Collapsed entities";
                lines.push(`List total: ${d.bundleTotal != null ? d.bundleTotal : "—"}`);
                lines.push(`On graph as dots: ${d.bundleDisplayed != null ? d.bundleDisplayed : "—"}`);
                lines.push(`Collapsed: ${d.bundleOverflow != null ? d.bundleOverflow : "—"}`);
                lines.push("Click for download / expand list");
            } else if (this.curationGraph && d.type === "entity") {
                title = String(d.key || "Entity");
                lines.push(`ID: ${d.key}`);
                lines.push(`Connections: ${conns}`);
                if (d.inBothClusters) lines.push("In both cluster entity lists");
                if (d.mergePreviewNew) lines.push("New from cluster 2 in merge preview");
            } else if (this.curationGraph && d.type === "cluster") {
                const n = Number(d.hubConnectionCount);
                const cnt = Number.isFinite(n) && n >= 0 ? n : 0;
                title = `${d.key || "Cluster"} · ${cnt} nodes`;
                lines.push(`Cluster: ${d.key}`);
                lines.push(`Total connected nodes: ${cnt}`);
            } else {
                title = String(this.labels[d.type] || d.type || "");
                lines.push(`Key: ${d.key}`);
                lines.push(`Connections: ${conns}`);
                if (d.domain) lines.push(`Domain: ${d.domain}`);
                if (d.entityCount != null) lines.push(`List size: ${d.entityCount}`);
                if (d.inBothClusters) lines.push("In both cluster lists");
            }
            this.graphInspector = { visible: true, title, lines };
        },

        truncateCurationGraphText(s, maxLen) {
            const t = String(s || "").trim();
            if (!t) return "—";
            if (t.length <= maxLen) return t;
            return `${t.slice(0, Math.max(1, maxLen - 1))}…`;
        },
        curationGraphNodeLabelLine(d, isLarge) {
            if (d.type === "entity_bundle" && d.key) {
                const k = String(d.key);
                return k.length <= 8 ? k : `${k.slice(0, 7)}…`;
            }
            if (d.type === "entity_bundle") {
                const n = Number(d.bundleTotal);
                return Number.isFinite(n) && n > 0 ? (n > 999 ? "999+" : String(n)) : "?";
            }
            if (d.type === "entity") {
                return this.truncateCurationGraphText(d.key, isLarge ? 7 : 11);
            }
            return (this.labels[d.type] || d.type || "").slice(0, 3).toUpperCase();
        },
        /** Total id count for a curation entity_group node (for labels). */
        curationEntityGroupIdCount(d) {
            if (!d || d.type !== "entity_group") return 0;
            const n = Number(d.idCountInNode);
            if (Number.isFinite(n) && n >= 0) return n;
            if (d.entityIds && d.entityIds.length) return d.entityIds.length;
            const subs = d.entitySubgroups || [];
            return subs.reduce((acc, s) => acc + ((s.entityIds && s.entityIds.length) || 0), 0);
        },
        graphNodeLabelLine(d, isLarge) {
            if (this.curationGraph) return this.curationGraphNodeLabelLine(d, isLarge);
            if (d.type === "entity_bundle" && d.key) {
                const k = String(d.key);
                return k.length <= 8 ? k : `${k.slice(0, 7)}…`;
            }
            if (d.type === "entity_bundle") {
                const n = Number(d.bundleTotal);
                return Number.isFinite(n) && n > 0 ? (n > 999 ? "999+" : String(n)) : "?";
            }
            if (d.type === "entity" && d.neighborType) return String(d.neighborType).slice(0, 3).toUpperCase();
            return (this.labels[d.type] || d.type || "").slice(0, 3).toUpperCase();
        },

        /** Stable HSL color for entity_neighbor_type (Graph Curation neighbor lists). */
        neighborTypeFill(d) {
            if (!this.curationGraph) return null;
            if (!d || d.neighborType == null) return null;
            const nt = String(d.neighborType).trim();
            if (!nt) return null;
            const ty = d.type;
            // Legend passes stub objects `{ neighborType }` without `type`; allow those when coloring by key.
            if (ty != null && ty !== "entity" && ty !== "entity_group") return null;
            let h = 0;
            const s = nt;
            for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
            const hue = h % 360;
            const light = this.isDark ? 52 : 44;
            return `hsl(${hue}, 58%, ${light}%)`;
        },

        nodeFill(d) {
            const byNeighbor = this.neighborTypeFill(d);
            if (byNeighbor) return byNeighbor;
            return this.getColor(d.type);
        },

        loadGraph(inputData) {
            this.clearGraphInspectorTimer();
            this.graphInspector = { visible: false, title: "", lines: [] };
            this.graphNodes = inputData.nodes.map(n => ({ ...n }));
            this.graphEdges = inputData.edges.map(e => {
                const src = typeof e.source === "object" ? e.source.id : e.source;
                const tgt = typeof e.target === "object" ? e.target.id : e.target;
                const out = { source: src, target: tgt };
                if (e.linkKind) out.linkKind = e.linkKind;
                return out;
            });

            this.hasGraph = true;
            this.updateStats();
            this.$nextTick(() => this.drawGraph());
        },

        nodeRadius(d) {
            const base = 14;
            if (d.type === "cluster") {
                if (this.curationGraph) {
                    const c = Number(d.hubConnectionCount) || 0;
                    return base + Math.min(16, 6 + Math.floor(c / 6));
                }
                const n = Number(d.entityCount) || 0;
                return base + Math.min(8, Math.floor(n / 2));
            }
            if (d.type === "entity_group") {
                const nd = (d.entitySubgroups && d.entitySubgroups.length) || 1;
                let r = base + Math.min(12, 2 + nd * 2);
                if (this.curationGraph) {
                    const lab = String(d.neighborType || d.key || "").length;
                    r += Math.min(8, Math.floor(lab / 8) + 4);
                }
                return r;
            }
            if (d.type === "entity_bundle") {
                const ov = Number(d.bundleOverflow) || 0;
                return base + Math.min(16, 4 + Math.log2(ov + 2) * 2.5);
            }
            return d.inBothClusters ? base + 3 : base;
        },

        /** Edges without `linkKind` (OLTP/OLAP/job viz) keep neutral gray styling. */
        linkStrokeDefault(e) {
            const k = e.linkKind;
            if (k === "pair") return this.isDark ? "#a78bfa" : "#6f42c1";
            if (k === "c1") return this.isDark ? "#6ea8fe" : "#0d6efd";
            if (k === "c2") return this.isDark ? "#63e6be" : "#0ca678";
            return this.isDark ? "#747474" : "#bdc3c7";
        },

        linkDashDefault(e) {
            return e.linkKind === "pair" ? "5 4" : "";
        },

        drawGraph() {
            const container = this.$refs.graphContainer;
            if (!container) return;

            const width = container.clientWidth;
            const height = container.clientHeight || 600;
            const isLarge = this.graphNodes.length > 200 || this.graphEdges.length > 220;

            d3.select(container).select("svg").remove();

            const svg = d3.select(container)
                .append("svg")
                .attr("width", "100%")
                .attr("height", "100%")
                .attr("viewBox", `0 0 ${width} ${height}`);

            const g = svg.append("g");

            this.zoomBehavior = d3.zoom()
                .scaleExtent([0.1, 2])
                .on("zoom", (event) => { g.attr("transform", event.transform); });
            svg.call(this.zoomBehavior);

            svg.on("click", () => {
                this.clearGraphInspectorTimer();
                this.graphInspector = { visible: false, title: "", lines: [] };
                this.clearHighlight(g);
                this.detailNode = null;
            });

            this.svg = svg;

            const chargeStrength = isLarge ? -120 : -300;
            const linkDist = isLarge ? 40 : 60;

            const nr = (d) => this.nodeRadius(d);
            this.simulation = d3.forceSimulation(this.graphNodes)
                .force("link", d3.forceLink(this.graphEdges).id(d => d.id).distance(linkDist))
                .force("charge", d3.forceManyBody().strength(chargeStrength).theta(isLarge ? 0.92 : 0.8))
                .force("collision", d3.forceCollide().radius(d => nr(d) + 3))
                .force("x", d3.forceX(width / 2).strength(0.08))
                .force("y", d3.forceY(height / 2).strength(0.08))
                .velocityDecay(isLarge ? 0.52 : 0.4)
                .alphaDecay(isLarge ? 0.055 : 0.02);

            const link = g.selectAll(".link")
                .data(this.graphEdges)
                .enter()
                .append("line")
                .attr("class", "link")
                .attr("stroke", d => this.linkStrokeDefault(d))
                .attr("stroke-width", d => (d.linkKind === "pair" ? 2.2 : 1.5))
                .attr("stroke-dasharray", d => this.linkDashDefault(d) || null)
                .attr("stroke-opacity", d => (d.linkKind === "pair" ? 0.75 : 0.55));

            const node = g.selectAll(".node")
                .data(this.graphNodes)
                .enter()
                .append("g")
                .attr("class", "node")
                .call(
                    d3.drag()
                        .on("start", (event, d) => {
                            if (!event.active) this.simulation.alphaTarget(0.3).restart();
                            d.fx = d.x;
                            d.fy = d.y;
                        })
                        .on("drag", (event, d) => {
                            d.fx = event.x;
                            d.fy = event.y;
                        })
                        .on("end", (event, d) => {
                            if (!event.active) this.simulation.alphaTarget(0);
                            if (!d.pinned) {
                                d.fx = null;
                                d.fy = null;
                            }
                        })
                );

            node.append("circle")
                .attr("r", d => nr(d))
                .attr("fill", d => this.nodeFill(d))
                .attr("stroke", d => this.nodeCircleStroke(d).color)
                .attr("stroke-width", d => this.nodeCircleStroke(d).width);

            const vm = this;
            const label = node.append("text")
                .attr("text-anchor", "middle")
                .attr("fill", this.isDark ? "#1a1d23" : "#fff")
                .attr("font-weight", "bold")
                .attr("pointer-events", "none");

            label.each(function(d) {
                const el = d3.select(this);
                el.selectAll("tspan").remove();
                el.attr("font-size", null);
                if (vm.curationGraph && d.type === "cluster") {
                    el.text("");
                    const name = vm.truncateCurationGraphText(String(d.key || "cluster"), isLarge ? 11 : 14);
                    const cnt = Number(d.hubConnectionCount);
                    const c = Number.isFinite(cnt) && cnt >= 0 ? cnt : 0;
                    el.append("tspan")
                        .attr("x", 0)
                        .attr("dy", "-0.52em")
                        .attr("font-size", isLarge ? "6px" : "8px")
                        .text(name);
                    el.append("tspan")
                        .attr("x", 0)
                        .attr("dy", "1.08em")
                        .attr("font-size", isLarge ? "7px" : "9px")
                        .text(`${c} nodes`);
                } else if (vm.curationGraph && d.type === "entity_group") {
                    el.text("");
                    const pair = vm.truncateCurationGraphText(String(d.neighborType || d.key || "—"), isLarge ? 10 : 14);
                    const n = vm.curationEntityGroupIdCount(d);
                    el.append("tspan")
                        .attr("x", 0)
                        .attr("dy", "-0.52em")
                        .attr("font-size", isLarge ? "6px" : "8px")
                        .text(pair);
                    el.append("tspan")
                        .attr("x", 0)
                        .attr("dy", "1.08em")
                        .attr("font-size", isLarge ? "7px" : "9px")
                        .text(`${n} ids`);
                } else {
                    const fs =
                        d.type === "entity_bundle"
                            ? (isLarge ? "7px" : "8px")
                            : isLarge
                                ? "7px"
                                : "9px";
                    el.attr("dy", "0.35em")
                        .attr("font-size", fs)
                        .text(vm.graphNodeLabelLine(d, isLarge));
                }
            });

            node.on("click", (event, d) => {
                event.stopPropagation();
                this.clearGraphInspectorTimer();
                this.graphInspector = { visible: false, title: "", lines: [] };
                this.highlightNeighbors(d, g);
                this.detailNode = d;
            });

            node.on("dblclick", (event, d) => {
                event.stopPropagation();
                d.pinned = !d.pinned;
                if (d.pinned) {
                    d.fx = d.x;
                    d.fy = d.y;
                } else {
                    d.fx = null;
                    d.fy = null;
                }
                d3.select(event.currentTarget).select("circle")
                    .attr("stroke", () => this.nodeCircleStroke(d).color)
                    .attr("stroke-width", () => this.nodeCircleStroke(d).width);
            });

            const tooltip = d3.select(container).select(".graph-tooltip");
            if (tooltip.empty()) {
                d3.select(container).append("div")
                    .attr("class", "graph-tooltip")
                    .style("opacity", 0)
                    .style("position", "absolute")
                    .style("pointer-events", "none"); 
            }

            node.on("mouseover", (event, d) => {
                if (this.curationGraph) {
                    this.clearGraphInspectorTimer();
                    this.setGraphInspectorFromNode(d);
                    return;
                }
                const tip = container.querySelector(".graph-tooltip");
                if (tip) {
                    const conns = this.connectionCountForNode(d);
                    let extra = "";
                    if (d.entityCount != null) extra += `<br>List size: ${d.entityCount}`;
                    if (d.inBothClusters) extra += `<br><em>In both cluster lists</em>`;
                    if (d.neighborType) extra += `<br>Type: <strong>${d.neighborType}</strong>`;
                    if (d.neighborDomain) extra += `<br>Neighbor domain: ${d.neighborDomain}`;
                    tip.innerHTML = `<strong>${this.labels[d.type] || d.type}</strong><br>Domain: ${d.domain}<br>Key: ${d.key}<br>Connections: ${conns}${extra}`;
                    tip.style.opacity = "1";
                    const rect = container.getBoundingClientRect();
                    tip.style.left = (event.clientX - rect.left + 15) + "px";
                    tip.style.top = (event.clientY - rect.top - 10) + "px";
                }
            });

            node.on("mouseout", () => {
                if (this.curationGraph) {
                    this.scheduleGraphInspectorHide();
                    return;
                }
                const tip = container.querySelector(".graph-tooltip");
                if (tip) tip.style.opacity = "0";
            });

            let tickCount = 0;
            const throttle = isLarge ? 3 : 1;

            this.simulation.on("tick", () => {
                tickCount++;
                if (tickCount % throttle !== 0) return;

                link
                    .attr("x1", d => d.source.x)
                    .attr("y1", d => d.source.y)
                    .attr("x2", d => d.target.x)
                    .attr("y2", d => d.target.y);
                node.attr("transform", d => `translate(${d.x},${d.y})`);
            });

            this.simulation.on("end", () => {
                this.fitToScreen();
            });
        },

        highlightNeighbors(d, g) {
            const neighborIds = new Set();
            neighborIds.add(d.id);
            this.graphEdges.forEach(e => {
                const s = typeof e.source === "object" ? e.source.id : e.source;
                const t = typeof e.target === "object" ? e.target.id : e.target;
                if (s === d.id) neighborIds.add(t);
                if (t === d.id) neighborIds.add(s);
            });

            g.selectAll(".node").style("opacity", n => neighborIds.has(n.id) ? 1 : 0.12);
            g.selectAll(".link")
                .style("opacity", e => {
                    const s = typeof e.source === "object" ? e.source.id : e.source;
                    const t = typeof e.target === "object" ? e.target.id : e.target;
                    return (s === d.id || t === d.id) ? 0.95 : 0.08;
                })
                .style("stroke", e => {
                    const s = typeof e.source === "object" ? e.source.id : e.source;
                    const t = typeof e.target === "object" ? e.target.id : e.target;
                    return (s === d.id || t === d.id) ? "#3498db" : this.linkStrokeDefault(e);
                })
                .style("stroke-dasharray", e => {
                    const s = typeof e.source === "object" ? e.source.id : e.source;
                    const t = typeof e.target === "object" ? e.target.id : e.target;
                    const hit = s === d.id || t === d.id;
                    return hit ? "none" : this.linkDashDefault(e) || null;
                })
                .style("stroke-width", e => {
                    const s = typeof e.source === "object" ? e.source.id : e.source;
                    const t = typeof e.target === "object" ? e.target.id : e.target;
                    return (s === d.id || t === d.id) ? 2.8 : e.linkKind === "pair" ? 2.2 : 1.5;
                })
                .style("stroke-opacity", e => {
                    const s = typeof e.source === "object" ? e.source.id : e.source;
                    const t = typeof e.target === "object" ? e.target.id : e.target;
                    const hit = s === d.id || t === d.id;
                    return hit ? 1 : e.linkKind === "pair" ? 0.35 : 0.3;
                });
        },

        clearHighlight(g) {
            if (!g) {
                if (!this.svg) return;
                g = this.svg.select("g");
            }
            g.selectAll(".node").style("opacity", 1);
            g.selectAll(".link")
                .style("opacity", null)
                .style("stroke", e => this.linkStrokeDefault(e))
                .style("stroke-width", e => (e.linkKind === "pair" ? 2.2 : 1.5))
                .style("stroke-dasharray", e => this.linkDashDefault(e) || null)
                .style("stroke-opacity", e => (e.linkKind === "pair" ? 0.75 : 0.55));
        },

        toggleType(type) {
            if (type === "__legend_overflow__") return;
            if (this.hiddenTypes.has(type)) this.hiddenTypes.delete(type);
            else this.hiddenTypes.add(type);
            this.applyTypeFilter();
        },

        legendKeyForNode(d) {
            if (this.curationGraph && d.entityLegendKey) return d.entityLegendKey;
            return d.type;
        },

        applyTypeFilter() {
            if (!this.svg) return;
            const g = this.svg.select("g");
            g.selectAll(".node").style("display", (d) => (this.hiddenTypes.has(this.legendKeyForNode(d)) ? "none" : null));
            g.selectAll(".link").style("display", (e) => {
                const s = typeof e.source === "object" ? e.source : { type: "" };
                const t = typeof e.target === "object" ? e.target : { type: "" };
                return this.hiddenTypes.has(this.legendKeyForNode(s)) || this.hiddenTypes.has(this.legendKeyForNode(t))
                    ? "none"
                    : null;
            });
        },

        zoomIn() {
            if (this.svg && this.zoomBehavior) {
                this.svg.transition().duration(300).call(this.zoomBehavior.scaleBy, 1.3);
            }
        },

        zoomOut() {
            if (this.svg && this.zoomBehavior) {
                this.svg.transition().duration(300).call(this.zoomBehavior.scaleBy, 0.7);
            }
        },

        fitToScreen() {
            if (!this.svg || !this.zoomBehavior || !this.$refs.graphContainer) return;
            const container = this.$refs.graphContainer;
            const width = container.clientWidth;
            const height = container.clientHeight || 600;

            let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
            this.graphNodes.forEach(n => {
                if (n.x !== undefined && n.y !== undefined) {
                    minX = Math.min(minX, n.x);
                    maxX = Math.max(maxX, n.x);
                    minY = Math.min(minY, n.y);
                    maxY = Math.max(maxY, n.y);
                }
            });

            if (!isFinite(minX)) return;

            const pad = 60;
            const gWidth = (maxX - minX) || 1;
            const gHeight = (maxY - minY) || 1;
            const scale = Math.min(width / (gWidth + 2 * pad), height / (gHeight + 2 * pad), 2);
            const tx = width / 2 - scale * (minX + gWidth / 2);
            const ty = height / 2 - scale * (minY + gHeight / 2);

            this.svg.transition().duration(500).call(
                this.zoomBehavior.transform,
                d3.zoomIdentity.translate(tx, ty).scale(scale)
            );
        },

        resetView() {
            this.clearGraphInspectorTimer();
            this.graphInspector = { visible: false, title: "", lines: [] };
            this.clearHighlight();
            this.hiddenTypes = new Set();
            this.applyTypeFilter();
            this.detailNode = null;
            this.fitToScreen();
        },

        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen;
            this.$nextTick(() => {
                if (this.svg) {
                    const container = this.$refs.graphContainer;
                    const w = container.clientWidth;
                    const h = container.clientHeight || 600;
                    this.svg.attr("viewBox", `0 0 ${w} ${h}`);
                }
                this.fitToScreen();
            });
        },

        updateStats() {
            const sharedEntities = this.graphNodes.filter(n => n.inBothClusters).length;
            const bundleOverflow = this.graphNodes
                .filter((n) => n.type === "entity_bundle")
                .reduce((sum, n) => sum + (Number(n.bundleOverflow) || 0), 0);
            const collapsedEntities = bundleOverflow;
            this.graphStats = {
                nodes: this.graphNodes.length,
                edges: this.graphEdges.length,
                sharedEntities,
                collapsedEntities,
            };
            const tc = {};
            this.graphNodes.forEach((n) => {
                const t = this.legendKeyForNode(n);
                tc[t] = (tc[t] || 0) + 1;
            });
            const sorted = Object.entries(tc).sort((a, b) => b[1] - a[1]);
            const mapped = sorted.map(([type, count]) => {
                if (type.startsWith("entity:")) {
                    const nt = type.slice(7);
                    if (nt.length) {
                        const fill =
                            nt === "user_composite"
                                ? this.colors["entity:user_composite"] || this.getColor("entity_group")
                                : this.neighborTypeFill({ neighborType: nt }) ||
                                  this.getColor("entity_group");
                        const label =
                            nt === "user_composite"
                                ? "Custom composite"
                                : this.curationGraph
                                  ? nt
                                  : `${nt} (entity)`;
                        return {
                            type,
                            label,
                            color: fill,
                            count,
                            legendOverflow: false,
                        };
                    }
                }
                return {
                    type,
                    label: this.labels[type] || type,
                    color: this.getColor(type),
                    count,
                    legendOverflow: false,
                };
            });
            if (this.curationGraph && mapped.length > CURATION_GRAPH_LEGEND_MAX) {
                const overflow = mapped.length - CURATION_GRAPH_LEGEND_MAX;
                this.legendItems = mapped.slice(0, CURATION_GRAPH_LEGEND_MAX).concat([
                    {
                        type: "__legend_overflow__",
                        label: `+${overflow} more kinds (use hover / graph; legend shows top ${CURATION_GRAPH_LEGEND_MAX})`,
                        color: "#95a5a6",
                        count: null,
                        legendOverflow: true,
                    },
                ]);
            } else {
                this.legendItems = mapped;
            }
        }
    }
};
</script>

<style scoped>
.graph-card { position: relative; }
.graph-fullscreen {
    position: fixed !important; top: 0; left: 0; right: 0; bottom: 0;
    z-index: 1050; margin: 0 !important; border-radius: 0 !important;
    display: flex; flex-direction: column;
}
.graph-fullscreen .graph-container { min-height: unset; flex: 1; }

.graph-toolbar {
    display: flex; justify-content: space-between; align-items: center;
    padding: 8px 14px; border-bottom: 1px solid #e3ebf6; background: #f9fbfd;
    flex-wrap: wrap; gap: 6px;
}
.toolbar-left { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.toolbar-right { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }

.toolbar-btn {
    background: none; border: 1px solid #d2ddec; border-radius: 4px;
    padding: 4px 8px; cursor: pointer; color: #6e84a3; font-size: 14px; transition: all 0.15s;
}
.toolbar-btn:hover { background: #e3ebf6; color: #12263f; }
.toolbar-divider { width: 1px; height: 18px; background: #d2ddec; margin: 0 4px; }
.graph-stats { font-size: 12px; color: #6e84a3; font-weight: 500; white-space: nowrap; }

.graph-inspector-bar {
    background: #eef3f9;
    color: #12263f;
    border-bottom-color: #e3ebf6 !important;
    line-height: 1.45;
}
.graph-inspector-title { color: #12263f; }

.graph-legend {
    display: flex; flex-wrap: wrap; gap: 4px; align-items: center;
    padding: 7px 14px; border-bottom: 1px solid #e3ebf6; background: #f9fbfd;
}
.legend-item {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 2px 8px; border: 1px solid #d2ddec; border-radius: 12px;
    background: white; cursor: pointer; font-size: 12px; transition: all 0.15s;
}
.legend-item:hover { border-color: #95aac9; }
.legend-item-disabled { opacity: 0.5; background: #f5f5f5; }
.legend-overflow {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border: 1px dashed #d2ddec;
    border-radius: 12px;
    background: #f9fbfd;
    font-size: 11px;
    max-width: 28rem;
}
.legend-dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
.legend-label { color: #12263f; font-weight: 500; }
.legend-count { color: #95aac9; }

.graph-container {
    position: relative; flex: 1; width: 100%; min-height: 420px;
    border: none; background-color: #fff; outline: none; cursor: grab;
}

.detail-panel {
    position: absolute; top: 80px; right: 12px;
    width: 260px; background: white; border: 1px solid #d2ddec;
    border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,0.1);
    z-index: 30; overflow: hidden;
}
.detail-panel-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 16px; background: #12263f; color: white; font-size: 13px;
}
.detail-panel-body { padding: 12px 16px; font-size: 12px; line-height: 1.8; }
.detail-panel--wide {
    width: min(360px, 92vw);
}
.detail-id-list {
    max-height: 280px;
    overflow-y: auto;
    border: 1px solid #e3ebf6;
    border-radius: 6px;
    padding: 4px 8px;
    background: #f9fbfd;
}

.detail-domain-list {
    max-height: 240px;
    overflow-y: auto;
    border: 1px solid #e3ebf6;
    border-radius: 6px;
    padding: 4px 10px;
    background: #f9fbfd;
}
.detail-entity-group-scroll {
    max-height: 14rem;
    overflow-y: auto;
}
.detail-label { color: #6e84a3; font-weight: 500; margin-right: 4px; }

.graph-tooltip {
    position: absolute; background-color: rgba(18, 38, 63, 0.92); color: #fff;
    padding: 10px 14px; border-radius: 6px; font-size: 12px;
    pointer-events: none; z-index: 20; line-height: 1.6;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15); max-width: 280px;
    opacity: 0; transition: opacity 0.15s;
}
</style>

<style lang="scss">
@use '../theme/scss/_user-variables.scss' as *;

body.dark-mode {

    /* Toolbar */
    .graph-toolbar {
        background: $dark-elevated-bg;
        border-bottom-color: $dark-border-color;
    }

    .graph-stats {
        color: $dark-text-primary;
    }

    .graph-inspector-bar {
        background: $dark-elevated-bg;
        color: $dark-text-primary;
        border-bottom-color: $dark-border-color !important;
    }
    .graph-inspector-title {
        color: $dark-text-primary;
    }
    .graph-inspector-lines {
        color: $dark-text-muted !important;
    }

    .toolbar-divider {
        background: #2d3748;
    }

    .dark-mode-btn {
        background: $dark-body-bg;
    }
    .dark-mode-btn:hover {
        background: $dark-border-color;
    }

    /* Legend */
    .graph-legend {
        background: $dark-elevated-bg;
        border-bottom-color: $dark-border-color;
    }

    .legend-item {
        background: #253344;
        border-color: #2d3748;
        color: #e2e8f0;
    }

    .legend-item:hover {
        border-color: #4a6080;
    }

    .legend-item-disabled {
        background: #1a2433;
        opacity: 0.4;
    }

    .legend-label {
        color: #e2e8f0;
    }

    .legend-count {
        color: #8899aa;
    }

    /* Graph container */
    .graph-container {
        background-color: $dark-card-bg;
    }

    /* Detail panel */
    .detail-panel {
        background: $dark-elevated-bg;
        border-color: $dark-border-color;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    }

    .detail-panel-header {
        background: $dark-border-color;
    }

    .detail-panel-body {
        color: $dark-text-primary;
    }

    .detail-label {
        color: $dark-text-muted;
    }

    .detail-id-list,
    .detail-domain-list {
        border-color: $dark-border-color;
        background: $dark-body-bg;
    }

    /* Tooltip */
    .graph-tooltip {
        background-color: rgba(13, 27, 42, 0.97);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    }
}
</style>
